import { Stack, Typography } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid-premium";
import { round } from "lodash";

import { FormatCurrency } from "@/hooks/useConfig/currency";
import { formatPercentageFromFraction } from "@/pages/scenario-builder/utils/formatPercentage";
import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";

import { BCPNode, getBusinessUnitName, getFunnelStage } from "../../../utils";
import { getBreakdownTable } from "../../../utils/spend-revenue-summary";

type ScenarioBreakdownTableRow = ReturnType<typeof getBreakdownTable>[number];

export interface ColumnDefinitionOptions {
  businessUnitToggle: BusinessUnitToggle;
  geoViewToggle: GeoViewToggle;
  formatCurrency: FormatCurrency;
  isGeoEnabled: boolean;
  mapBcpName: (bcp: BCPNode) => BCPNode;
  metricType: MetricType;
}

const BusinessUnitCell = ({
  value,
  mapBcpName,
}: {
  value: BCPNode;
  mapBcpName: (bcp: BCPNode) => BCPNode;
}) => {
  const funnelStage = getFunnelStage(value);

  const mappedValue = mapBcpName(value);

  if (funnelStage === "brand") {
    return (
      <Typography fontWeight={600} variant="body2">
        {mappedValue.brand}
      </Typography>
    );
  }

  if (funnelStage === "category") {
    return (
      <Stack>
        <Typography fontWeight={600} variant="body2">
          {mappedValue.category}
        </Typography>
        <Typography variant="body2">{mappedValue.brand}</Typography>
      </Stack>
    );
  }

  return (
    <Stack>
      <Typography fontWeight={600} variant="body2">
        {mappedValue.product}
      </Typography>
      <Typography variant="body2">
        {mappedValue.category} &gt; {mappedValue.brand}
      </Typography>
    </Stack>
  );
};

export const getColumnDefinitions = (
  activeColumns: string[],
  options: ColumnDefinitionOptions
): GridColDef<ScenarioBreakdownTableRow>[] => {
  const {
    businessUnitToggle,
    geoViewToggle,
    formatCurrency,
    isGeoEnabled,
    mapBcpName,
    metricType,
  } = options;

  const columns: GridColDef<ScenarioBreakdownTableRow>[] = [];

  // Add business unit column if funnel stage is split
  if (businessUnitToggle === "split") {
    columns.push({
      field: "business_unit",
      headerName: "Business Unit",
      valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) =>
        row.business_unit ? getBusinessUnitName(mapBcpName(row.business_unit)) : undefined,
      renderCell: (params: GridRenderCellParams<ScenarioBreakdownTableRow>) => {
        return (
          params.row.business_unit && (
            <BusinessUnitCell value={params.row.business_unit} mapBcpName={mapBcpName} />
          )
        );
      },
      sortable: true,
      type: "string",
      flex: 1,
      minWidth: 200,
      hideable: !activeColumns.includes("business_unit"),
    });
  }

  // Always add channel column
  columns.push({
    field: "channel",
    headerName: "Channel",
    valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) => row.channel,
    type: "string",
    flex: 1,
    hideable: !activeColumns.includes("channel"),
  });

  // Add funnel stage column if funnel stage is split
  if (businessUnitToggle === "split") {
    columns.push({
      field: "funnel_stage",
      headerName: "Funnel Stage",
      valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) =>
        getFunnelStage({
          brand: row.brand || "",
          category: row.category || "",
          product: row.product || "",
        }),
      type: "string",
      flex: 1,
      hideable: !activeColumns.includes("funnel_stage"),
    });
  }

  // Add geography column if geo is enabled and geo view is split
  if (isGeoEnabled && geoViewToggle === "split") {
    columns.push({
      field: "geography",
      headerName: "Geography",
      valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) =>
        row.geography || "N/A",
      type: "string",
      flex: 1,
      hideable: !activeColumns.includes("geography"),
    });
  }

  // Always add spend column
  columns.push({
    field: "spend",
    headerName: "Marketing Spend",
    valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) => row.spend,
    valueFormatter: ({ value }: GridValueFormatterParams) => formatCurrency(value),
    type: "number",
    flex: 1,
    sortable: true,
    hideable: !activeColumns.includes("spend"),
  });

  // Always add revenue column
  columns.push({
    field: "revenue",
    headerName: "Simulated Revenue",
    valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) => row.revenue,
    valueFormatter: ({ value }: GridValueFormatterParams) => formatCurrency(value),
    type: "number",
    flex: 1,
    sortable: true,
    hideable: !activeColumns.includes("revenue"),
  });

  if (metricType === MetricType.MARKETING_ROI) {
    columns.push({
      field: "roi",
      headerName: "Simulated ROI",
      valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) => row.roi,
      valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentageFromFraction(value),
      sortComparator: (_a, _b, param1, param2) => param1.value - param2.value,
      type: "number",
      flex: 1,
      sortable: true,
      hideable: !activeColumns.includes("roi"),
    });
  }
  if (metricType === MetricType.SALES_UNITS) {
    columns.push({
      field: "roi",
      headerName: "Simulated ROI",
      valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) => row.roi,
      valueFormatter: ({ value }: GridValueFormatterParams) => formatPercentageFromFraction(value),
      sortComparator: (_a, _b, param1, param2) => param1.value - param2.value,
      type: "number",
      flex: 1,
      sortable: true,
      hideable: !activeColumns.includes("roi"),
    });
    columns.push({
      field: "volume",
      headerName: "Simulated Sales",
      valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) => row.volume,
      type: "number",
      flex: 1,
      sortable: true,
      hideable: !activeColumns.includes("volume"),
    });
  }

  if (metricType === MetricType.RETURN_ON_AD_SPEND) {
    columns.push({
      field: "roas",
      headerName: "Simulated ROAS",
      valueGetter: ({ row }: GridValueGetterParams<ScenarioBreakdownTableRow>) =>
        round((row.revenue / row.spend) * 100, 2),
      valueFormatter: ({ value }: GridValueFormatterParams) => (value ? `${value}%` : "N/A"),
      type: "number",
      flex: 1,
      sortable: true,
      hideable: !activeColumns.includes("roas"),
    });
  }

  return columns;
};
